import { Icon } from "@iconify/react";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    getSessions,
    createSession,
    updateSession,
    deleteSession,
    restoreSession,
    permanentDeleteSession,
    getDeletedSessions
} from "../redux/session/sessionSlice";
import { toast } from "react-toastify";
import { getSpeakers } from "../redux/userSpeaker/userSpeakerSlice";
import { formatDate, formatTime, truncateText } from "../helper/helper";
import Select from "react-select";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import SessionViewModal from "../pages/sessions/SessionViewModal";
import { Modal } from "bootstrap";
import useDebounce from "../hook/useDebounce";
import { useLocationFilter } from "../../src/context/LocationContext";
import PaginationHelper from "../helper/PaginationHelper";

const REQUIRED_FIELDS = [
    "locationId",
    "eventTypeId",
    "category",
    "title",
    "sessionDate",
    "startTime",
    "endTime"
];

const sessionFields = [
    { key: "title", label: "Session Title", type: "text", required: true },
    { key: "locationId", label: "Event Location", type: "select", required: true },
    { key: "eventTypeId", label: "Session Type", type: "select", required: true },
    { key: "category", label: "Session Category", type: "select", required: true },
    { key: "description", label: "Session Description", type: "text" },
    { key: "sessionDate", label: "Session Date", type: "date", required: true },
    { key: "startTime", label: "Start Time", type: "time", required: true },
    { key: "endTime", label: "End Time", type: "time", required: true },
    { key: "room", label: "Hall No / Room No", type: "text" },
    { key: "speakerIds", label: "Speakers", type: "select" },
    { key: "tags", label: "# Tags", type: "text" }
];

const quillModules = {
    toolbar: [
        [{ header: [1, 2, 3, false] }],
        ["bold", "italic", "underline"],
        [{ list: "ordered" }, { list: "bullet" }],
        ["link"],
        ["clean"]
    ]
};

const categoryOptions = [
    "Keynote",
    "Panel",
    "CentreStage",
    "Roundtable"
];

const initialState = {
    title: "",
    locationId: "",
    eventTypeId: "",
    category: "",
    description: "",
    sessionDate: "",
    startTime: "",
    endTime: "",
    room: "",
    tags: "",
    speakerIds: []
};

const SessionLayer = () => {

    const dispatch = useDispatch()
    const { selectedLocation } = useLocationFilter();

    const {
        sessions = [],
        deletedSessions = [],
        total = 0,
        totalPages = 1
    } = useSelector((state) => state.session);

    const { speakers = {} } = useSelector((state) => state.speaker);
    const speakerList = speakers?.items || [];
    const { locations = [], locationTypes = [] } = useSelector((state) => state.eventLocation);

    const [viewTrash, setViewTrash] = useState(false)
    const [search, setSearch] = useState("")
    const [entries, setEntries] = useState(50)
    const [currentPage, setCurrentPage] = useState(1)
    const [filteredTypes, setFilteredTypes] = useState([]);

    const [formData, setFormData] = useState(initialState)
    const [isEdit, setIsEdit] = useState(false)
    const [editId, setEditId] = useState(null)
    const [deleteId, setDeleteId] = useState(null)
    const [viewSession, setViewSession] = useState(null);
    const debouncedSearch = useDebounce(search, 1000);
    const [speakerSearch, setSpeakerSearch] = useState("");
    const debouncedSpeakerSearch = useDebounce(speakerSearch, 1000);
    const [selectedSpeakers, setSelectedSpeakers] = useState([]);

    const formatLabel = (key) =>
        key.replace(/([A-Z])/g, " $1").replace(/^./, str => str.toUpperCase())

    useEffect(() => {
        if (!viewTrash) {
            dispatch(
                getSessions({
                    page: currentPage,
                    limit: entries,
                    search: debouncedSearch?.trim() || "",
                    locationId: selectedLocation?.value || ""
                })
            );
        } else {
            dispatch(
                getSessions({
                    page: currentPage,
                    limit: entries,
                    search: debouncedSearch?.trim() || "",
                    locationId: selectedLocation?.value || ""
                })
            );
        }
    }, [dispatch, viewTrash, currentPage, entries, debouncedSearch, selectedLocation]);

    useEffect(() => {
        dispatch(getSpeakers({ search: debouncedSpeakerSearch }));
    }, [dispatch, debouncedSpeakerSearch]);

    useEffect(() => {

        if (!formData.locationId) {
            setFilteredTypes([]);
            return;
        }

        const selectedLocation = locations.find(
            (loc) => loc.id === formData.locationId
        );

        if (selectedLocation?.eventTypes) {
            setFilteredTypes(selectedLocation.eventTypes);
        }

    }, [formData.locationId, locations]);


    const paginatedSessions = sessions;
    const totalEntries = total;
    const openCreate = () => {
        setFormData(initialState)
        setSelectedSpeakers([]);
        setIsEdit(false)
    }

    const openView = (session) => {
        setViewSession(session);

        setTimeout(() => {
            const modal = new Modal(
                document.getElementById("viewSessionModal")
            );

            modal.show();
        }, 100);
    };


    const openEdit = (session) => {
        console.log(session, 'session');
        setFormData({
            title: session.title || "",
            description: session.description || "",
            category: session.category || "",
            room: session.room || "",
            tags: Array.isArray(session.tags)
                ? session.tags.join(", ")
                : session.tags || "",

            locationId: session.locationId || "",
            eventTypeId: session.eventTypeId || "",

            sessionDate: session.sessionDate
                ? session.sessionDate.split("T")[0]
                : "",

            startTime: session.startTime
                ? session.startTime.slice(0, 5)
                : "",

            endTime: session.endTime
                ? session.endTime.slice(0, 5)
                : "",

            speakerIds: session.speakers
                ? session.speakers.map(s => s.id)
                : []
        });
        setSelectedSpeakers(
            session.speakers
                ? session.speakers.map(s => ({
                    value: s.id,
                    label: s.fullName
                }))
                : []
        );

        setEditId(session.id);
        setIsEdit(true);
    };

    const handleSubmit = () => {

        for (let field of sessionFields) {
            if (field.required) {
                const value = formData[field.key];

                // Special case for arrays
                if (Array.isArray(value) && value.length === 0) {
                    toast.error(`${field.label} is required`);
                    return;
                }

                // Normal check
                if (!value || value.toString().trim() === "") {
                    toast.error(`${field.label} is required`);
                    return;
                }
            }
        }
        if (formData.startTime >= formData.endTime) {
            toast.error("End time must be greater than start time");
            return;
        }
        const payload = {
            ...formData,
            speakerIds: formData.speakerIds || [],
            tags: formData.tags
                ? formData.tags.split(",").map(tag => tag.trim())
                : []

        };

        const action = isEdit
            ? updateSession({ id: editId, data: payload })
            : createSession(payload);

        dispatch(action)
            .unwrap()
            .then((res) => {
                toast.success(res?.message || (isEdit ? "Session updated successfully" : "Session created successfully"));

                dispatch(
                    getSessions({
                        page: currentPage,
                        limit: entries,
                        search: debouncedSearch,
                        locationId: selectedLocation?.value || ""
                    })
                );

                document.querySelector("#exampleModal .btn-close")?.click();

            })
            .catch((err) => {

                const message =
                    err?.message ||
                    err?.response?.data?.message ||
                    err?.payload?.message ||
                    "Something went wrong";

                toast.error(message);
            });
    };
    const speakerOptions = speakerList.map((sp) => ({
        value: sp.id,
        label: sp.fullName
    }));
    return (
        <>

            <div className="card h-100 p-0 radius-12">

                <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center justify-content-between flex-wrap gap-3">

                    <div className="d-flex align-items-center gap-3 flex-wrap">

                        {/* Show */}
                        <div className="d-flex align-items-center gap-2">
                            <span className="text-md fw-medium text-secondary-light">Show</span>

                            <select
                                className="form-select form-select-sm h-40-px"
                                style={{ width: "80px" }}
                                value={entries}
                                onChange={(e) => {
                                    setEntries(Number(e.target.value));
                                    setCurrentPage(1);
                                }}
                            >
                                <option value="50">50</option>
                                <option value="100">100</option>
                                <option value="150">150</option>
                                <option value="200">200</option>
                            </select>
                        </div>

                        {/* Search */}
                        <div style={{ width: "220px" }}>
                            <input
                                type="text"
                                className="form-control h-40-px"
                                placeholder="Search"
                                value={search}
                                onChange={(e) => {
                                    setSearch(e.target.value);
                                    setCurrentPage(1);
                                }}
                            />
                        </div>

                    </div>

                    <div className="d-flex align-items-center" style={{ marginLeft: "auto" }}>
                        <button
                            className="btn btn-primary text-sm btn-sm px-16 py-10 radius-8 d-flex align-items-center gap-2"
                            data-bs-toggle="modal"
                            data-bs-target="#exampleModal"
                            onClick={openCreate}
                        >
                            <Icon icon="ic:baseline-plus" />
                            Add Session
                        </button>
                    </div>

                </div>

                <div className="card-body p-12">

                    <div className="table-responsive">

                        <table className="table bordered-table sm-table">

                            <thead>
                                <tr>
                                    <th>S.L</th>
                                    <th>Session Title</th>
                                    {/* <th>Session Description</th> */}
                                    {/* <th>Event Type</th> */}
                                    <th>Session Type</th>
                                    <th>Category</th>
                                    <th>Date</th>
                                    <th>Start</th>
                                    <th>End</th>
                                    {/* <th>Room</th> */}
                                    {/* <th>Status</th> */}
                                    <th className="text-center">Action</th>
                                </tr>
                            </thead>

                            <tbody>
                                {paginatedSessions.length > 0 ? (
                                    paginatedSessions.map((s, index) => (
                                        <tr key={s.id}>
                                            <td>{(currentPage - 1) * entries + index + 1}</td>

                                            <td>{truncateText(s.title, 30)}</td>

                                            <td>{truncateText(s.eventLocation?.name || "-", 30)}</td>

                                            <td>{s.category}</td>

                                            <td>{formatDate(s.sessionDate)}</td>

                                            <td>{formatTime(s.startTime)}</td>

                                            <td>{formatTime(s.endTime)}</td>

                                            <td className="text-center">
                                                <div className="d-flex align-items-center gap-10 justify-content-center">
                                                    <button
                                                        className="bg-info-focus text-info-600 bg-hover-info-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                                                        onClick={() => openView(s)}
                                                    >
                                                        <Icon icon="lucide:eye" />
                                                    </button>

                                                    <button
                                                        className="bg-success-focus text-success-600 bg-hover-success-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                                                        data-bs-toggle="modal"
                                                        data-bs-target="#exampleModal"
                                                        onClick={() => openEdit(s)}
                                                    >
                                                        <Icon icon="lucide:edit" />
                                                    </button>

                                                    <button
                                                        className="remove-item-btn bg-danger-focus bg-hover-danger-200 text-danger-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                                                        data-bs-toggle="modal"
                                                        data-bs-target="#deleteModal"
                                                        onClick={() => setDeleteId(s.id)}
                                                    >
                                                        <Icon icon="fluent:delete-24-regular" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="8" className="text-center py-40">
                                            <div className="d-flex flex-column align-items-center justify-content-center">

                                                <Icon
                                                    icon="mdi:database-off-outline"
                                                    width="40"
                                                    className="text-secondary mb-8"
                                                />

                                                <span className="fw-semibold text-secondary">
                                                    {selectedLocation
                                                        ? "No sessions found for selected location"
                                                        : "No sessions available"}
                                                </span>

                                                <small className="text-muted">
                                                    {selectedLocation
                                                        ? "Try selecting a different location"
                                                        : "Try creating a new session"}
                                                </small>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>

                        </table>


                    </div>
                    {/* PAGINATION */}
                    <PaginationHelper
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={setCurrentPage}
                        totalItems={total}
                        pageSize={entries}
                    />
                </div>

            </div>

            {/* CREATE / UPDATE MODAL */}

            <div className="modal fade" id="exampleModal">

                <div className="modal-dialog modal-lg modal-dialog-centered">

                    <div className="modal-content radius-16">

                        <div className="modal-header">

                            <h5>{isEdit ? "Update Session" : "Add Session"}</h5>

                            <button className="btn-close" data-bs-dismiss="modal" />

                        </div>

                        <div className="modal-body">

                            <div className="row">

                                {sessionFields.map((field) => {

                                    let inputElement;

                                    if (field.key === "category") {

                                        inputElement = (
                                            <select
                                                className="form-control"
                                                value={formData.category || ""}
                                                onChange={(e) =>
                                                    setFormData({ ...formData, category: e.target.value })
                                                }
                                            >
                                                <option value="">Select Category</option>
                                                {categoryOptions.map((cat, i) => (
                                                    <option key={i} value={cat}>{cat}</option>
                                                ))}
                                            </select>
                                        );

                                    } else if (field.key === "locationId") {

                                        inputElement = (
                                            <select
                                                className="form-control"
                                                value={formData.locationId || ""}
                                                onChange={(e) =>
                                                    setFormData({
                                                        ...formData,
                                                        locationId: e.target.value,
                                                        eventTypeId: ""
                                                    })
                                                }

                                            >
                                                <option value="">Select Event Location</option>
                                                {locations.map((loc) => (
                                                    <option key={loc.id} value={loc.id}>{loc.name}</option>
                                                ))}
                                            </select>
                                        );

                                    } else if (field.key === "eventTypeId") {

                                        inputElement = (
                                            <select
                                                className="form-control"
                                                value={formData.eventTypeId || ""}
                                                onChange={(e) =>
                                                    setFormData({ ...formData, eventTypeId: e.target.value })
                                                }
                                            >
                                                <option value="">Select Session Type</option>
                                                {filteredTypes.map((lt) => (
                                                    <option key={lt.id} value={lt.id}>
                                                        {lt.name}
                                                    </option>
                                                ))}

                                            </select>
                                        );

                                    } else if (field.key === "speakerIds") {

                                        inputElement = (
                                            <Select
                                                isMulti
                                                options={speakerOptions}
                                                value={selectedSpeakers}

                                                onChange={(selected) => {
                                                    setSelectedSpeakers(selected || []);
                                                    setFormData({
                                                        ...formData,
                                                        speakerIds: selected ? selected.map(s => s.value) : []
                                                    });
                                                }}

                                                onInputChange={(inputValue) => {
                                                    setSpeakerSearch(inputValue);
                                                }}

                                                onMenuOpen={() => {
                                                    if (!speakerList.length) {
                                                        dispatch(getSpeakers({ search: "" }));
                                                    }
                                                }}

                                                isClearable
                                                placeholder="Search & select speakers..."
                                            />
                                        );

                                    }
                                    else if (field.key === "description") {

                                        inputElement = (
                                            <ReactQuill
                                                theme="snow"
                                                modules={quillModules}
                                                value={formData.description || ""}
                                                onChange={(value) =>
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        description: value
                                                    }))
                                                }
                                            />
                                        );

                                    }
                                    else {

                                        inputElement = (
                                            <input
                                                className="form-control"
                                                type={field.type}
                                                value={formData[field.key] || ""}
                                                onChange={(e) =>
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        [field.key]: e.target.value
                                                    }))
                                                }
                                            />
                                        );

                                    }

                                    return (
                                        <div
                                            className={field.key === "description"
                                                ? "col-md-12 mb-3"
                                                : "col-md-6 mb-3"}
                                            key={field.key}
                                        >
                                            <label className="form-label text-sm text-primary-light">
                                                {field.label}
                                                {field.required && <span className="text-danger"> *</span>}
                                            </label>

                                            {inputElement}
                                        </div>
                                    );

                                })}

                            </div>

                            <div className="text-end mt-3">

                                <button
                                    className="btn btn-primary"
                                    onClick={handleSubmit}
                                >

                                    {isEdit ? "Update" : "Create"}

                                </button>

                            </div>

                        </div>

                    </div>

                </div>

            </div>

            {/* DELETE CONFIRM MODAL */}

            <div
                className="modal fade"
                id="deleteModal"
                tabIndex={-1}
                aria-hidden="true"
            >
                <div className="modal-dialog modal-dialog-centered">

                    <div className="modal-content radius-16">

                        <div className="modal-header">
                            <h5>Delete Session</h5>
                            <button
                                className="btn-close"
                                data-bs-dismiss="modal"
                            />
                        </div>

                        <div className="modal-body text-center">

                            <p className="mb-3">
                                Are you sure you want to delete this session?
                            </p>

                            <div className="d-flex justify-content-center gap-3">

                                <button
                                    className="btn btn-secondary"
                                    data-bs-dismiss="modal"
                                >
                                    Cancel
                                </button>

                                <button
                                    className="btn btn-danger"
                                    onClick={() => {

                                        dispatch(deleteSession(deleteId))
                                            .unwrap()
                                            .then((res) => {

                                                toast.success(res?.message || "Session deleted")

                                                dispatch(
                                                    getSessions({
                                                        page: currentPage,
                                                        limit: entries,
                                                        search: debouncedSearch,
                                                        locationId: selectedLocation?.value || ""
                                                    })
                                                );
                                                setDeleteId(null)

                                                document.querySelector("#deleteModal .btn-close")?.click()

                                            })
                                            .catch((err) => {

                                                toast.error(
                                                    err?.message ||
                                                    err?.response?.data?.message ||
                                                    "Delete failed"
                                                )

                                            })

                                    }}
                                >
                                    Delete
                                </button>

                            </div>

                        </div>

                    </div>

                </div>
            </div>
            <SessionViewModal session={viewSession} />

        </>

    )

}

export default SessionLayer